// Story 16.6 — elegir una contraseña nueva con el link que generó un admin.
// Pública (fuera del AppShell). El token viaja en el fragmento
// (`/reset-password#<token>`, ver `lib/hash-token`). Al terminar quedan
// cerradas todas las sesiones anteriores y se abre una nueva.

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { type FormEvent, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ApiError, api } from '@/lib/api-client';
import { forgetHashToken, readHashToken } from '@/lib/hash-token';

const STORAGE_KEY = 'revel.reset-token';

export function ResetPasswordPage(): React.ReactElement {
  const [token] = useState(() => readHashToken(STORAGE_KEY));
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const lookup = useQuery({
    queryKey: ['password-reset', token],
    queryFn: () => api.post<{ email: string }>('/api/v1/auth/password-reset/lookup', { token }),
    enabled: Boolean(token),
    retry: false,
  });

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (password.length < 8) return setError('La contraseña necesita al menos 8 caracteres.');
    if (password !== confirm) return setError('Las contraseñas no coinciden.');
    setError(null);
    setSubmitting(true);
    try {
      await api.post('/api/v1/auth/password-reset', { token, password });
      setPassword('');
      setConfirm('');
      forgetHashToken(STORAGE_KEY);
      qc.clear();
      toast.success('Contraseña cambiada');
      await navigate({ to: '/' });
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : 'No se pudo cambiar.');
    } finally {
      setSubmitting(false);
    }
  }

  let body: React.ReactNode;
  if (!token) {
    body = <CardContent className="text-sm">Este link de reset no sirve.</CardContent>;
  } else if (lookup.isLoading) {
    body = <CardContent className="text-sm text-muted-foreground">Cargando…</CardContent>;
  } else if (lookup.isError || !lookup.data) {
    body = (
      <CardContent className="text-sm">
        {lookup.error instanceof ApiError && lookup.error.status === 404
          ? 'El link ya se usó o venció. Pide uno nuevo a tu administrador.'
          : 'No se pudo cargar el link. Inténtalo de nuevo en un momento.'}
      </CardContent>
    );
  } else {
    body = (
      <form onSubmit={handleSubmit}>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="reset-email">Email</Label>
            <Input id="reset-email" value={lookup.data.email} readOnly disabled />
          </div>
          <div className="space-y-2">
            <Label htmlFor="reset-password">Contraseña nueva</Label>
            <Input
              id="reset-password"
              type="password"
              autoComplete="new-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              maxLength={72}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="reset-confirm">Repite la contraseña</Label>
            <Input
              id="reset-confirm"
              type="password"
              autoComplete="new-password"
              required
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              maxLength={72}
            />
          </div>
          {error && (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          )}
        </CardContent>
        <CardFooter>
          <Button type="submit" className="w-full" disabled={submitting}>
            {submitting ? 'Guardando…' : 'Guardar contraseña'}
          </Button>
        </CardFooter>
      </form>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Nueva contraseña</CardTitle>
          <CardDescription>Se cierran todas tus sesiones abiertas.</CardDescription>
        </CardHeader>
        {body}
      </Card>
    </div>
  );
}
